import { useCallback, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { searchTurnkeyAddress } from '../services/turnkeyAddressSearch.api'
import type { TurnkeyAddressSearchItem } from '../services/turnkeyAddressSearch.types'

function parseKeywords(value: string) {
    const result: string[] = []
    const seen = new Set<string>()

    value
        .split(/[\n\r,;]+/)
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => {
            const key = item.toLowerCase()
            if (seen.has(key)) return
            seen.add(key)
            result.push(item)
        })

    return result
}

export function useTurnkeyAddressSearch() {
    const notify = useNotify()
    const t = useTranslate()

    const [loading, setLoading] = useState(false)
    const [searched, setSearched] = useState(false)
    const [items, setItems] = useState<TurnkeyAddressSearchItem[]>([])
    const [searchedTotal, setSearchedTotal] = useState(0)
    const [matchedTotal, setMatchedTotal] = useState(0)
    const [missedTotal, setMissedTotal] = useState(0)

    const search = useCallback(
        async (keywordText: string) => {
            const keywords = parseKeywords(keywordText)

            if (!keywords.length) {
                notify(t('module.turnkeyAddressSearch.inputRequired', { _: 'Please enter address / email / wallet' }), { type: 'warning' })
                return
            }

            setLoading(true)
            setSearched(true)
            setSearchedTotal(keywords.length)

            try {
                const response = await searchTurnkeyAddress(keywords)
                const nextItems = response.items || []

                setItems(nextItems)
                setSearchedTotal(response.searchedTotal ?? keywords.length)
                setMatchedTotal(response.matchedTotal ?? response.total ?? nextItems.length)
                setMissedTotal(response.missedTotal ?? Math.max(keywords.length - nextItems.length, 0))
            } catch (error) {
                console.error(error)
                notify(t('module.turnkeyAddressSearch.searchFailed', { _: 'Search failed' }), { type: 'error' })
                setItems([])
                setMatchedTotal(0)
                setMissedTotal(keywords.length)
            } finally {
                setLoading(false)
            }
        },
        [notify, t],
    )

    const reset = useCallback(() => {
        setItems([])
        setSearched(false)
        setSearchedTotal(0)
        setMatchedTotal(0)
        setMissedTotal(0)
    }, [])

    return {
        loading,
        searched,
        items,
        total: items.length,
        searchedTotal,
        matchedTotal,
        missedTotal,
        search,
        reset,
    }
}
